import { Badge, Breadcrumbs, Card, CardContent, PageHeader, Spinner } from '@estac1/ui';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { api } from '../lib/api.js';
import { subscriptionStatusLabel } from '../lib/status.js';
import { BillingSection } from './client-detail/billing-section.js';
import { HealthSection } from './client-detail/health-section.js';
import { LotMap } from './client-detail/lot-map.js';
import { type ClientOverview, day, when } from './client-detail/types.js';
import { UsageSection } from './client-detail/usage-section.js';
import { ViewAsOwnerButton } from './client-detail/view-as-owner-button.js';
import { statusVariant } from './clients/utils.js';

const ROLE: Record<string, string> = {
  patio: 'Pátio',
  caixa: 'Caixa',
  convenio: 'Loja conveniada',
};

/**
 * Um cliente pelos olhos do suporte (#656): plano, uso, o que falta
 * configurar, o que deu errado e quem mexeu, para ajudar o dono numa ligação.
 */
export const ClientDetailScreen = () => {
  const { lotId } = useParams({ from: '/layout/clients/$lotId' });
  const overview = useQuery({
    queryKey: ['admin-client-overview', lotId],
    queryFn: () => api.request<ClientOverview>(`/v1/admin/clients/${lotId}/overview`),
  });
  if (overview.isLoading) return <Spinner />;
  if (!overview.data) {
    return <p className="text-sm text-muted-fg">Não foi possível abrir este cliente.</p>;
  }
  const { lot, owners, team, activity } = overview.data;
  const sub = lot.subscription;

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <PageHeader
        breadcrumbs={
          <Breadcrumbs items={[{ label: 'Clientes', href: '/clients' }, { label: lot.name }]} />
        }
        title={lot.name}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={statusVariant(sub.status)}>{subscriptionStatusLabel(sub.status)}</Badge>
            <ViewAsOwnerButton lotId={lot.id} />
          </div>
        }
      />
      <div className="grid gap-3 md:grid-cols-2">
        <Card>
          <CardContent className="space-y-1 pt-6 text-sm">
            <p>
              {[lot.address, lot.city && lot.state ? `${lot.city} - ${lot.state}` : lot.city]
                .filter(Boolean)
                .join(', ') || 'Sem endereço'}
            </p>
            <p className="text-muted-fg">
              {lot.vacancies != null ? `${lot.vacancies} vagas` : 'Sem vagas definidas'} · cliente
              desde {day(lot.createdAt)}
            </p>
            {sub.trialEndsAt && sub.status === 'trial' && <p>Teste até {when(sub.trialEndsAt)}</p>}
            {sub.ticketBlockAt && (
              <p className="text-danger">Bloqueio de tickets em {when(sub.ticketBlockAt)}</p>
            )}
            <div className="pt-2">
              {owners.map((o) => (
                <p key={o.email}>
                  <span className="font-medium">{o.name}</span> · {o.email}
                  <span className="block text-xs text-muted-fg">
                    último acesso: {when(o.lastLoginAt)}
                  </span>
                </p>
              ))}
            </div>
          </CardContent>
        </Card>
        <LotMap location={overview.data.location} />
      </div>
      <UsageSection usage={overview.data.usage} />
      <HealthSection data={overview.data} />
      <BillingSection lotId={lot.id} />
      <div className="grid gap-3 md:grid-cols-2">
        <Card>
          <CardContent className="space-y-2 pt-6">
            <p className="text-sm font-medium">Equipe</p>
            {team.length === 0 && <p className="text-sm text-muted-fg">Nenhum operador.</p>}
            <ul className="space-y-1 text-sm">
              {team.map((t) => (
                <li key={`${t.name}-${t.role}`} className="flex justify-between gap-2">
                  <span>
                    {t.name} · {ROLE[t.role] ?? t.role}
                    {!t.active && ' (inativo)'}
                  </span>
                  <span className="shrink-0 text-xs text-muted-fg">{when(t.lastSeenAt)}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="space-y-2 pt-6">
            <p className="text-sm font-medium">Atividade recente</p>
            {activity.length === 0 && <p className="text-sm text-muted-fg">Nada registrado.</p>}
            <ul className="space-y-1 text-sm">
              {activity.map((a) => (
                <li key={`${a.at}-${a.action}`} className="flex justify-between gap-2">
                  <span className="truncate">
                    {a.action} · {a.entity}
                    {a.actorEmail ? ` · ${a.actorEmail}` : ` · ${a.actorType}`}
                  </span>
                  <span className="shrink-0 text-xs text-muted-fg">{when(a.at)}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
